import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Avatar,
} from '@mui/material';
import {
  TrendingUp,
  Co2Outlined,
  PrecisionManufacturing,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  description: string;
  date: string;
  category: string;
  author: string;
  authorImage: string;
  image: string;
  readTime: string;
  tags: string[];
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'The Future of African Manufacturing: Trends to Watch in 2025',
    excerpt: 'Discover the key trends shaping the future of manufacturing in Africa and how Monio is leading the charge in innovation and sustainability.',
    description: 'As we move into 2025, the African manufacturing sector is experiencing unprecedented growth and transformation. From advanced automation to sustainable practices, the industry is evolving rapidly. At Monio, we\'re at the forefront of these changes, implementing cutting-edge technologies while maintaining our commitment to quality and environmental responsibility.',
    date: 'January 15, 2025',
    category: 'Industry Insights',
    author: 'John Smith',
    authorImage: '/assets/team/john-smith.jpg',
    image: '/assets/blog/african-manufacturing.jpg',
    readTime: '5 min read',
    tags: ['Manufacturing', 'Innovation', 'Africa', 'Technology'],
  },
  {
    id: '2',
    title: 'Sustainable Manufacturing: How Monio is Reducing Our Environmental Impact',
    excerpt: 'Learn about our comprehensive approach to sustainable manufacturing and the eco-friendly practices we\'ve implemented across our facilities.',
    description: 'Sustainability isn\'t just a buzzword at Monio—it\'s a core principle that guides every aspect of our manufacturing processes. From renewable energy sources to waste reduction initiatives, we\'re committed to minimizing our environmental footprint while delivering exceptional products.',
    date: 'January 10, 2025',
    category: 'Sustainability',
    author: 'Lisa Rodriguez',
    authorImage: '/assets/team/lisa-rodriguez.jpg',
    image: '/assets/blog/sustainable-manufacturing.jpg',
    readTime: '4 min read',
    tags: ['Sustainability', 'Green Manufacturing', 'Eco-friendly'],
  },
  {
    id: '3',
    title: 'Quality Assurance in Modern Manufacturing: Our Approach to Excellence',
    excerpt: 'Explore our rigorous quality control processes and how we ensure every product meets the highest standards of excellence.',
    description: 'Quality is non-negotiable at Monio. Our comprehensive quality assurance system combines traditional craftsmanship with modern technology to ensure every product that leaves our facilities meets international standards. From raw material inspection to final product testing, we maintain strict quality controls at every stage.',
    date: 'January 8, 2025',
    category: 'Quality Control',
    author: 'Sarah Williams',
    authorImage: '/assets/team/sarah-williams.jpg',
    image: '/assets/blog/quality-control.jpg',
    readTime: '6 min read',
    tags: ['Quality Control', 'Excellence', 'Standards'],
  },
  {
    id: '4',
    title: 'Smart Furniture Manufacturing: Integrating Technology with Design',
    excerpt: 'Discover how we\'re revolutionizing furniture manufacturing by combining smart technology with timeless design principles.',
    description: 'The furniture industry is evolving, and Monio is leading the way with our innovative smart furniture manufacturing capabilities. We integrate cutting-edge technology with ergonomic design to create furniture that\'s not just beautiful, but intelligent and functional.',
    date: 'January 5, 2025',
    category: 'Innovation',
    author: 'Mike Johnson',
    authorImage: '/assets/team/mike-johnson.jpg',
    image: '/assets/blog/smart-furniture.jpg',
    readTime: '7 min read',
    tags: ['Smart Furniture', 'Innovation', 'Design', 'Technology'],
  },
  {
    id: '5',
    title: 'Textile Manufacturing Excellence: From Fiber to Finished Product',
    excerpt: 'Take a deep dive into our textile manufacturing processes and learn how we create premium fabrics for diverse applications.',
    description: 'Our textile manufacturing division combines traditional weaving techniques with modern technology to produce high-quality fabrics for fashion, industrial, and commercial applications. From sustainable materials to advanced finishing processes, we ensure every textile meets our exacting standards.',
    date: 'January 3, 2025',
    category: 'Textiles',
    author: 'David Chen',
    authorImage: '/assets/team/david-chen.jpg',
    image: '/assets/blog/textile-manufacturing.jpg',
    readTime: '5 min read',
    tags: ['Textiles', 'Fabric', 'Manufacturing', 'Quality'],
  },
  {
    id: '6',
    title: 'Building Strong Partnerships: How We Collaborate with Our Clients',
    excerpt: 'Learn about our collaborative approach to manufacturing and how we work closely with clients to deliver customized solutions.',
    description: 'At Monio, we believe that the best results come from strong partnerships. We work closely with our clients throughout the entire manufacturing process, from initial concept to final delivery. This collaborative approach ensures that every project meets our clients\' exact specifications and exceeds their expectations.',
    date: 'January 1, 2025',
    category: 'Partnerships',
    author: 'Jane Doe',
    authorImage: '/assets/team/jane-doe.jpg',
    image: '/assets/blog/partnerships.jpg',
    readTime: '4 min read',
    tags: ['Partnerships', 'Collaboration', 'Client Relations'],
  },
];

const categories = [
  { name: 'All Posts', value: 'all', icon: <Co2Outlined /> },
  { name: 'Industry Insights', value: 'Industry Insights', icon: <TrendingUp /> },
  { name: 'Sustainability', value: 'Sustainability', icon: <Co2Outlined /> },
  { name: 'Quality Control', value: 'Quality Control', icon: <PrecisionManufacturing /> },
  { name: 'Innovation', value: 'Innovation', icon: <AutoAwesome /> },
  { name: 'Textiles', value: 'Textiles', icon: <Co2Outlined /> },
  { name: 'Partnerships', value: 'Partnerships', icon: <Co2Outlined /> },
];

export const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      color: 'text.primary',
      pt: 12,
      pb: 16,
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
          color: 'white',
          py: 8,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 3,
            }}
          >
            Monio Manufacturing Blog
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            Industry insights, company news, and manufacturing expertise
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', opacity: 0.8 }}>
            Stay updated with the latest trends in manufacturing, learn about our innovative processes, 
            and discover how we're shaping the future of African manufacturing.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Category Filter */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Explore Our Articles
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            flexWrap="wrap"
            sx={{ mb: 4 }}
          >
            {categories.map((category) => (
              <Chip
                key={category.value}
                label={category.name}
                onClick={() => setSelectedCategory(category.value)}
                icon={category.icon}
                sx={{
                  background: selectedCategory === category.value 
                    ? 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: selectedCategory === category.value ? 'white' : 'text.primary',
                  border: selectedCategory === category.value 
                    ? 'none' 
                    : '1px solid rgba(99, 102, 241, 0.2)',
                  '&:hover': {
                    background: selectedCategory === category.value 
                      ? 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)'
                      : 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
        
        {/* Blog Posts Grid */}
        <Grid container spacing={4}>
          {filteredPosts.map((post) => (
            <Grid item xs={12} md={6} lg={4} key={post.id}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 200,
                    background: `linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                />
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={post.category} 
                      size="small" 
                      sx={{ 
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        fontWeight: 500,
                      }} 
                    />
                    <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                      {post.readTime}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {post.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {post.excerpt}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar 
                      src={post.authorImage} 
                      sx={{ width: 32, height: 32, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {post.author}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.date}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    {post.tags.slice(0, 2).map((tag) => (
                      <Chip 
                        key={tag} 
                        label={tag} 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          borderColor: 'rgba(99, 102, 241, 0.3)',
                          color: '#6366f1',
                        }} 
                      />
                    ))}
                  </Stack>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      border: '2px solid #6366f1',
                      color: '#6366f1',
                      '&:hover': {
                        border: '2px solid #4f46e5',
                        background: 'rgba(99, 102, 241, 0.04)',
                      },
                    }}
                  >
                    Read Full Article
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Newsletter Signup */}
        <Box sx={{ mt: 8, p: 4, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', borderRadius: 3 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600 }}>
            Stay Updated with Monio
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Subscribe to our newsletter for the latest manufacturing insights, company news, and industry trends.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                },
              }}
            >
              Subscribe to Newsletter
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
